import React, { useEffect, useMemo, useState } from 'react';
import { Form, Input } from 'antd';
import creditCardTyp from 'credit-card-type';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import dayjs from 'dayjs';

import i18n from '../../../assets/languages/configs';
import visaCardIcon from '../../../assets/imgs/ic_visa-card.svg?react';
import masterCardIcon from '../../../assets/imgs/ic_master-card.svg?react';
import jcbCardIcon from '../../../assets/imgs/ic_jcb-card.svg?react';
import americanExpressIcon from '../../../assets/imgs/ic_american-card.svg?react';
import dinersClubIcon from '../../../assets/imgs/ic_diners-card.svg?react';
import discoverIcon from '../../../assets/imgs/ic_discover.svg?react';
import supportCard from '../../../assets/imgs/supportCard.png';
import {
    calcTotalPrice,
    getValueByLanguage,
    isEmptyObject,
    isReserveTicket,
    showNotificationDialog,
} from '../../../utils';
import {
    DEV_ENV_FLAG,
    EMAIL_REGEX_PATTERN,
    GMO_SHOP_ID,
    PAYMENT_TYPE,
    localStorageKeys,
} from '../../../utils/Constants';
import usePurchaseTicket from '../../../hooks/usePurchaseTicket';
import useLocalizedMoney from '../../../hooks/useLocalizedMoney';
import useLocalizedTime from '../../../hooks/useLocalizedTime';
import Header from '../../../components/header/Header';
import CenterLoading from '../../../components/CenterLoading/CenterLoading';
import TextBreaks from '../../../components/TextBreaks/TextBreaks';
import styles from './styles.module.css';
import AvailableWeekdays from '../../../components/AvailableWeekdays/AvailableWeekdays';
import { LocalStorageManager } from '../../../utils/LocalStorageManager/LocalStorageManager';
import { useFujiAuth } from '../../../auth/useFujiAuth';
import CustomSelect from '../../../components/CustomSelect/CustomSelect';
import CustomAntButton from '../../../components/CustomAntButton/CustomAntButton';
import { SelectNumber, TicketInfo } from 'types';

function BuyCreditCardPaymentScreen() {
    const Multipayment = window.Multipayment;
    const [isCreditCardNo, setIsCreditCardNo] = useState(true);
    const [isValidExpireYear, setIsValidExpireYear] = useState(true);
    const [isValidExpireMonth, setIsValidExpireMonth] = useState(true);
    const [creditCardType, setCreditCardType] = useState('');
    const [securityCodeInfo, setSecurityCodeInfo] = useState({
        size: 3,
    });
    const [ticketInfo, setTicketInfo] = useState<TicketInfo | null>(null);
    const [selectedPeopleNumber, setSelectedPeopleNumber] = useState<SelectNumber | null | undefined>({});
    const [cardGetTokenLoading, setCardGetTokenLoading] = useState(false);
    const [form] = Form.useForm();
    const { isLoading, purchaseWithCard, showOfflineError, WarningConfirmModal } = usePurchaseTicket();
    const { formatTime } = useLocalizedTime();
    const { formatMoney } = useLocalizedMoney();
    const { language } = i18n;
    const location = useLocation();
    const { t } = useTranslation();
    const { reservationCombo, userForm, anonymousFormAnswers } = location.state ?? {};
    const { user } = useFujiAuth();
    const { email } = user ?? {};
    let isComposing = false;

    const storageManager = useMemo(() => new LocalStorageManager('BuyCreditCardPaymentScreen'), []);

    const initialFormValues = useMemo(() => {
        const isPrivateRelayEmail = (email?: string) => {
            if (!email) {
                return false;
            }
            const domain = email.split('@')[1];
            return domain === 'privaterelay.appleid.com';
        };
        const userEmail = !isPrivateRelayEmail(email) ? email : null;
        return { email: userEmail, remember: true };
    }, [email]);

    useEffect(() => {
        let ticketInfo: TicketInfo | null = null;
        let selectNumber: SelectNumber | null | undefined = {};
        if (location.state) {
            ticketInfo = location.state.ticketInfo;
            selectNumber = location.state.selectNumber;
        } else {
            ticketInfo = storageManager.getStorage(localStorageKeys.TICKET_INFO_DATA);
            selectNumber = ticketInfo?.selectNumber;
        }
        setTicketInfo(ticketInfo);
        setSelectedPeopleNumber(selectNumber);
    }, [location, storageManager]);

    const totalPrice = useMemo(() => {
        return calcTotalPrice(selectedPeopleNumber, {
            adult: ticketInfo?.priceAdult ?? 0,
            custom1: ticketInfo?.priceCustom1 ?? 0,
            custom2: ticketInfo?.priceCustom2 ?? 0,
            custom3: ticketInfo?.priceCustom3 ?? 0,
        });
    }, [ticketInfo, selectedPeopleNumber]);

    useEffect(() => {
        if (form.isFieldTouched('cardNo')) {
            void form.validateFields(['cardNo']);
        }
    }, [isCreditCardNo, form]);

    //有効期限の年の初期値を計算する
    const expireYearOptions = useMemo(() => {
        const currentYear = dayjs().year();
        const options = [];
        for (let index = currentYear; index < currentYear + 10; index++) {
            options.push({
                label: index.toString(),
                value: index.toString(),
            });
        }
        return options;
    }, []);

    //有効期限の月の初期値を計算する
    const expireMonthOptions = useMemo(() => {
        const options = [];
        for (let index = 1; index <= 12; index++) {
            options.push({
                label: index.toString().padStart(2, '0'),
                value: index.toString().padStart(2, '0'),
            });
        }
        return options;
    }, []);

    //クレジットカード番号を変更の処理
    const handleCardNoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const cardNo = e.target.value;
        if (!cardNo || cardNo.length <= 0) {
            setIsCreditCardNo(false);
            setCreditCardType('');
            setSecurityCodeInfo({
                size: 3,
            });
            return;
        }

        //入力した値は数字の形かどうかチェック
        const RE_DIGIT = new RegExp(/^\d+$/);
        let newCardNo = '';
        for (let index = 0; index < cardNo.length; index++) {
            const char = cardNo[index];
            const isTargetValueDigit = RE_DIGIT.test(char);
            if (isTargetValueDigit) {
                newCardNo += char;
            }
        }

        //クレジットカード番号が不明の時
        const creditCardInfo = creditCardTyp(newCardNo)[0];
        if (creditCardInfo == null) {
            setSecurityCodeInfo({
                size: 3,
            });
            if (!DEV_ENV_FLAG) {
                setIsCreditCardNo(false);
            }
            setCreditCardType('');
            form.setFieldValue('cardNo', newCardNo);
            return;
        }

        //クレジットカードのタイプによって番号のmaxLengthを取得
        let creditCardMaxLength = Math.max(...creditCardInfo.lengths);
        //クレジットカードのタイプによって空間を追加する
        creditCardInfo.gaps.forEach((sliceIndex, index) => {
            const currentIndex = sliceIndex + index;
            if (newCardNo.length - 1 >= currentIndex) {
                creditCardMaxLength += 1;
                newCardNo = newCardNo.slice(0, currentIndex) + ' ' + newCardNo.slice(currentIndex);
            }
        });

        //入力した番号はクレジットカード番号の最大桁数をチェック
        if (newCardNo.length > creditCardMaxLength) {
            newCardNo = newCardNo.slice(0, creditCardMaxLength);
        }
        setIsCreditCardNo(true);
        setCreditCardType(creditCardInfo.niceType);
        setSecurityCodeInfo({
            size: creditCardInfo.code.size,
        });
        form.setFieldValue('cardNo', newCardNo);
    };

    //有効期限の年を変更
    const handleExpireYearChange = (expireYear: string) => {
        form.setFieldValue('expireYear', expireYear);
        void form.validateFields(['expireYear']);
        setIsValidExpireYear(!!form.getFieldValue(['expireYear']));
    };

    //有効期限の月を変更
    const handleExpireMonthChange = (expireMonth: string) => {
        form.setFieldValue('expireMonth', expireMonth);
        void form.validateFields(['expireMonth']);
        setIsValidExpireMonth(!!form.getFieldValue(['expireMonth']));
    };

    //セキュリティコードを変更の処理
    const handleSecurityCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const securityCode = e.target.value;
        if (!securityCode || securityCode.length <= 0) {
            return;
        }

        //入力した値は数字の形かどうかチェック
        const RE_DIGIT = new RegExp(/^\d+$/);
        let newSecurityCode = '';
        for (let index = 0; index < securityCode.length; index++) {
            const char = securityCode[index];
            const isTargetValueDigit = RE_DIGIT.test(char);
            if (isTargetValueDigit) {
                newSecurityCode += char;
            }
        }
        form.setFieldValue('securityCode', newSecurityCode);
    };

    //form submit
    interface FormValues {
        cardNo: string;
        holderName: string;
        securityCode: string;
        expireYear: string;
        expireMonth: string;
        email: string;
    }
    const handleSubmit = ({ cardNo, holderName, securityCode, expireYear, expireMonth, email }: FormValues) => {
        if (!navigator.onLine) {
            showOfflineError();
            return;
        }
        if (!Multipayment) {
            return;
        }
        setCardGetTokenLoading(true);

        try {
            const newCardNo = cardNo.split(' ').join('');
            const newExpire = expireYear.slice(-2) + expireMonth;
            Multipayment.init(GMO_SHOP_ID);
            const requestFormat = {
                cardno: newCardNo,
                holdername: holderName,
                securitycode: securityCode,
                expire: newExpire,
            };
            Multipayment.getToken(
                requestFormat,
                (result: { resultCode: number | string; tokenObject?: { token?: string } }) => {
                    const { resultCode, tokenObject } = result ?? {};

                    if (resultCode === 121 || resultCode === 122 || resultCode === '121' || resultCode === '122') {
                        setCardGetTokenLoading(false);
                        showNotificationDialog('error', '', t('error-message.buy-ticket.invalid-cvc'));
                        return;
                    }

                    setCardGetTokenLoading(false);
                    void purchaseWithCard({
                        salesTicketId: ticketInfo?.salesTicketId,
                        number: selectedPeopleNumber,
                        cardToken: tokenObject?.token,
                        paymentType: PAYMENT_TYPE.creditCard,
                        reservationCombo,
                        userForm,
                        email,
                        anonymousFormAnswers,
                    });
                },
            );
        } catch {
            setCardGetTokenLoading(false);
        }
    };

    //決済ボタンクリック
    const handlePaymentClick = () => {
        setIsValidExpireYear(!!form.getFieldValue(['expireYear']));
        setIsValidExpireMonth(!!form.getFieldValue(['expireMonth']));
    };

    const convertFullWidthToHalfWidth = (str: string) => {
        return (
            str
                .replace(/[\uFF01-\uFF5E]/g, (ch) => String.fromCharCode(ch.charCodeAt(0) - 0xfee0))
                // eslint-disable-next-line no-irregular-whitespace
                .replace(/　/g, ' ')
        );
    };

    const handleHolderNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!isComposing) {
            const newInputValue = convertFullWidthToHalfWidth(e.target.value).toUpperCase();
            form.setFieldValue('holderName', newInputValue);
        }
    };

    const handleCompositionStart = () => {
        isComposing = true;
    };

    const handleCompositionEnd = (event: React.CompositionEvent<HTMLInputElement>) => {
        isComposing = false;
        handleHolderNameChange({
            target: event.target as HTMLInputElement,
        } as React.ChangeEvent<HTMLInputElement>);
    };

    return (
        <>
            <WarningConfirmModal />
            <Header title={t('header.buy-ticket')} />
            {!isEmptyObject(ticketInfo) && (
                <div className={`${styles.wrapper} content-container scrollable`}>
                    {(isLoading || cardGetTokenLoading) && <CenterLoading overFooter />}
                    <div className={styles.infoGroup}>
                        {/*券名*/}
                        <div className={styles.ticketName}>
                            <TextBreaks text={getValueByLanguage(ticketInfo?.ticketName, language)} />
                        </div>
                        {/*サブタイトル*/}
                        <div className={styles.subtitle}>
                            <TextBreaks text={getValueByLanguage(ticketInfo?.subtitle, language)} />
                        </div>
                        {/*使用開始可能曜日*/}
                        {ticketInfo?.comboItems.length === 1 && !isReserveTicket(ticketInfo) && (
                            <AvailableWeekdays
                                ticket={{ ...ticketInfo, ...ticketInfo.comboItems[0] }}
                                className={styles.textContent}
                                labelClass={styles.label}
                                dividerClass={styles.divider}
                                valueClass={styles.value}
                            />
                        )}
                        {/*人数*/}
                        {
                            <div className={styles.textContent}>
                                <span className={styles.label}>{t('ticket-detail.number-label')}</span>
                                <span className={styles.divider}>:</span>
                                <div className={styles.value}>
                                    {(selectedPeopleNumber?.adult ?? 0) > 0 && (
                                        <span className={styles.numberContext}>
                                            {t('ticket-detail.number', {
                                                label: t('ticket-detail.adult-label'),
                                                number: selectedPeopleNumber?.adult,
                                            })}
                                        </span>
                                    )}
                                    {(selectedPeopleNumber?.custom1 ?? 0) > 0 && (
                                        <span className={styles.numberContext}>
                                            {t('ticket-detail.number', {
                                                label: getValueByLanguage(ticketInfo?.custom1Name, language),
                                                number: selectedPeopleNumber?.custom1,
                                            })}
                                        </span>
                                    )}
                                    {(selectedPeopleNumber?.custom2 ?? 0) > 0 && (
                                        <span className={styles.numberContext}>
                                            {t('ticket-detail.number', {
                                                label: getValueByLanguage(ticketInfo?.custom2Name, language),
                                                number: selectedPeopleNumber?.custom2,
                                            })}
                                        </span>
                                    )}
                                    {(selectedPeopleNumber?.custom3 ?? 0) > 0 && (
                                        <span className={styles.numberContext}>
                                            {t('ticket-detail.number', {
                                                label: getValueByLanguage(ticketInfo?.custom3Name, language),
                                                number: selectedPeopleNumber?.custom3,
                                            })}
                                        </span>
                                    )}
                                </div>
                            </div>
                        }
                        {/*料金*/}
                        {
                            <div className={styles.textContent}>
                                <span className={styles.label}>{t('ticket-detail.price')}</span>
                                <span className={styles.divider}>:</span>
                                <div className={styles.value}>{formatMoney(totalPrice, 'JPY')}</div>
                            </div>
                        }
                        {/*払戻期限*/}
                        {ticketInfo?.refundDeadlineAt && (
                            <div className={styles.textContent}>
                                <span className={styles.label}>{t('ticket-detail.refund-deadline')}</span>
                                <span className={styles.divider}>:</span>
                                <div className={styles.value}>
                                    {t('daytime.until', { time: formatTime(ticketInfo.refundDeadlineAt, 'lll') })}
                                </div>
                            </div>
                        )}
                    </div>
                    <div className={styles.supportCard}>
                        <img src={supportCard} alt="no images" className={styles.supportCardIcon} />
                    </div>
                    <Form
                        form={form}
                        className={styles.registerForm}
                        initialValues={initialFormValues}
                        onFinish={handleSubmit}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                (e.target as HTMLInputElement).blur();
                            }
                        }}
                        autoComplete="off"
                    >
                        <label className={styles.cardFormLabel} htmlFor="cardNo">
                            {t('credit-card.card-number')}
                        </label>
                        <Form.Item
                            name="cardNo"
                            rules={[
                                {
                                    required: true,
                                    message: t('validate.enter-card-number') ?? '',
                                },
                                {
                                    message: t('validate.correct-card-number') ?? '',
                                    validator: (_, value) => {
                                        if (!value || isCreditCardNo) {
                                            return Promise.resolve();
                                        } else {
                                            return Promise.reject();
                                        }
                                    },
                                },
                            ]}
                        >
                            <Input
                                name="cardNo"
                                size="large"
                                inputMode="numeric"
                                className={styles.input}
                                placeholder="0000 0000 0000 0000"
                                onChange={handleCardNoChange}
                                suffix={<CardInfoIcon cardType={creditCardType} />}
                            />
                        </Form.Item>
                        <label className={styles.cardFormLabel} htmlFor="holderName">
                            {t('credit-card.holder-name')}
                        </label>
                        <Form.Item
                            name="holderName"
                            rules={[
                                {
                                    required: true,
                                    message: t('validate.enter-holder-name') ?? '',
                                },
                                {
                                    // eslint-disable-next-line no-irregular-whitespace
                                    pattern: /^[A-Za-z 　]+$/,
                                    message: t('validate.correct-holder-name') ?? '',
                                },
                                {
                                    message: t('validate.enter-holder-name') ?? '',
                                    validator: (_, value) => {
                                        if (!value || value.trim()) {
                                            return Promise.resolve();
                                        } else {
                                            return Promise.reject();
                                        }
                                    },
                                },
                                {
                                    message: t('validate.max-holder-name') ?? '',
                                    max: 50,
                                },
                            ]}
                        >
                            <Input
                                name="holderName"
                                size="large"
                                className={styles.input}
                                placeholder="TARO RYOKO"
                                onChange={handleHolderNameChange}
                                onCompositionStart={handleCompositionStart}
                                onCompositionEnd={handleCompositionEnd}
                            />
                        </Form.Item>

                        <>
                            <label className={styles.cardFormLabel} htmlFor="email">
                                {t('form.email')}
                            </label>
                            <Form.Item
                                name="email"
                                rules={[
                                    {
                                        pattern: EMAIL_REGEX_PATTERN,
                                        message: t('validate.correct-email') ?? '',
                                    },
                                    {
                                        required: true,
                                        message: t('validate.email') ?? '',
                                    },
                                ]}
                            >
                                <Input
                                    name="email"
                                    size="large"
                                    className={styles.input}
                                    placeholder="example@example.com"
                                />
                            </Form.Item>
                        </>

                        <Form.Item>
                            <div className={styles.security}>
                                <div className={styles.expire}>
                                    <label className={styles.cardFormLabel}>{t('credit-card.expiry-date')}</label>
                                    <div className={styles.expireValue}>
                                        <Form.Item
                                            name="expireMonth"
                                            className={styles.expireItem}
                                            rules={[
                                                {
                                                    required: true,
                                                    message: t('validate.select-month') ?? '',
                                                },
                                            ]}
                                        >
                                            <div className={styles.expireMonth}>
                                                <CustomSelect
                                                    listHeight={200}
                                                    name="expireMonth"
                                                    size="large"
                                                    variant="plain"
                                                    className={`${styles.selectExpire} ${
                                                        !isValidExpireMonth ? styles.error : ''
                                                    }`}
                                                    placeholder="MM"
                                                    onChange={handleExpireMonthChange}
                                                    options={expireMonthOptions}
                                                    getPopupContainer={(trigger: HTMLElement) => trigger.parentNode}
                                                />
                                            </div>
                                        </Form.Item>
                                        <p className={styles.expireItem}>{' / '}</p>
                                        <Form.Item
                                            name="expireYear"
                                            className={styles.expireItem}
                                            rules={[
                                                {
                                                    required: true,
                                                    message: t('validate.select-year') ?? '',
                                                },
                                            ]}
                                        >
                                            <div className={styles.expireYear}>
                                                <CustomSelect
                                                    listHeight={200}
                                                    size="large"
                                                    variant="plain"
                                                    className={`${styles.selectExpire} ${
                                                        !isValidExpireYear ? styles.error : ''
                                                    }`}
                                                    placeholder="YYYY"
                                                    onChange={handleExpireYearChange}
                                                    options={expireYearOptions}
                                                    getPopupContainer={(trigger: HTMLElement) => trigger.parentNode}
                                                />
                                            </div>
                                        </Form.Item>
                                    </div>
                                </div>
                                <div className={styles.securityCode}>
                                    <label className={styles.cardFormLabel} htmlFor="securityCode">
                                        {t('credit-card.security-code')}
                                    </label>
                                    <Form.Item
                                        name="securityCode"
                                        rules={[
                                            {
                                                required: true,
                                                message: t('validate.enter-security-code') ?? '',
                                            },
                                        ]}
                                    >
                                        <Input
                                            name="securityCode"
                                            inputMode="numeric"
                                            size="large"
                                            className={styles.input}
                                            placeholder="000"
                                            onChange={handleSecurityCodeChange}
                                            maxLength={securityCodeInfo.size}
                                        />
                                    </Form.Item>
                                </div>
                            </div>
                            <p className={styles.note}>{t('credit-card.note')}</p>
                        </Form.Item>

                        <Form.Item>
                            <CustomAntButton onClick={handlePaymentClick} block htmlType="submit">
                                {t('button.purchase')}
                            </CustomAntButton>
                        </Form.Item>
                    </Form>
                </div>
            )}
        </>
    );
}

const CardInfoIcon = ({ cardType }: { cardType: string }) => {
    let CreditCardIcon: React.FC<React.SVGProps<SVGSVGElement>> | null = null;
    switch (cardType) {
        case 'Visa':
            CreditCardIcon = visaCardIcon;
            break;
        case 'Mastercard':
            CreditCardIcon = masterCardIcon;
            break;
        case 'JCB':
            CreditCardIcon = jcbCardIcon;
            break;
        case 'American Express':
            CreditCardIcon = americanExpressIcon;
            break;
        case 'Diners Club':
            CreditCardIcon = dinersClubIcon;
            break;
        case 'Discover':
            CreditCardIcon = discoverIcon;
            break;
        default:
            return null;
    }
    return <CreditCardIcon className={styles.creditCardIcon} />;
};

export default BuyCreditCardPaymentScreen;
